import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { EditType } from 'components/organisms/EditPanel/types';
import type {
  Attribute,
  EditPanelAction,
  Variant,
} from 'screens/VariantManagement/components/EditSidePanel/types';

type EditPanelStateDetails = Attribute | Variant;

export interface EditPanelDetailsState {
  type: EditType;
  details: EditPanelStateDetails | null;
}

export interface EditPanelActionState {
  type: EditType;
  action: EditPanelAction | null;
}

export interface EditPanelState {
  isEditPanelOpen: {
    [key in EditType]: boolean;
  };
  editPanelDetailsState: {
    [key in EditType]: EditPanelStateDetails | null;
  };
  editPanelAction: {
    [key in EditType]: EditPanelAction | null;
  };
}

const initialState: EditPanelState = {
  isEditPanelOpen: {
    [EditType.VARIANT]: false,
    [EditType.ATTRIBUTE]: false,
  },
  editPanelDetailsState: {
    [EditType.VARIANT]: null,
    [EditType.ATTRIBUTE]: null,
  },
  editPanelAction: {
    [EditType.VARIANT]: null,
    [EditType.ATTRIBUTE]: null,
  },
};

const editVariantManagementPanelSlice = createSlice({
  name: 'editVariantManagementPanel',
  initialState,
  reducers: {
    openEditPanel: (state, data: PayloadAction<EditType>) => {
      state.isEditPanelOpen[data.payload] = true;
    },
    setEditPanelDetailsState: (
      state,
      { payload: editPanelDetailsState }: PayloadAction<EditPanelDetailsState>,
    ) => {
      state.editPanelDetailsState[editPanelDetailsState.type] =
        editPanelDetailsState.details;
    },
    setEditPanelAction: (
      state,
      { payload: editPanelAction }: PayloadAction<EditPanelActionState>,
    ) => {
      state.editPanelAction[editPanelAction.type] = editPanelAction.action;
    },
    closeEditPanel: (state, data: PayloadAction<EditType>) => {
      state.isEditPanelOpen[data.payload] = false;
    },

    closeEditPanelAndReset: (state, data: PayloadAction<EditType>) => {
      state.isEditPanelOpen[data.payload] = false;
      state.editPanelDetailsState[data.payload] = null;
      state.editPanelAction[data.payload] = null;
    },
  },
});

export const {
  openEditPanel,
  closeEditPanel,
  setEditPanelDetailsState,
  closeEditPanelAndReset,
  setEditPanelAction,
} = editVariantManagementPanelSlice.actions;

export default editVariantManagementPanelSlice.reducer;
